/**
 * One calendar-day × schedule-row cell: droppable for bar moves, optional right-click
 * for empty-cell context menu (Create Scope / Paste Scope), and styling for weekends /
 * out-of-job-range / placeholder rows.
 * Droppables stay enabled whenever the row has a job (including missing job date range)
 * so DnD always has targets; out-of-range is visual-only and drops clamp in move logic.
 *
 * Locked days stay **droppable** so drag-end collision resolves to that cell; {@link useScheduleBlockActions}
 * then rejects the move and shows “Cannot move scope into locked days”. Disabling the droppable
 * made drops fall through with no payload and skipped that toast.
 *
 * `weekGapBorderAfter`: thick **right** rule when this day is Friday before a hidden Sat–Sun gap (next visible column is Monday).
 * When `dayLocked`, Dispatch marks this calendar day locked — cell is muted and non-interactive (rough parity with Dispatch).
 */
import { useDroppable } from "@dnd-kit/core";
import {
  SCHEDULE_HIDDEN_WEEKEND_GAP_BORDER,
  SCHEDULE_HIDDEN_WEEKEND_GAP_BORDER_THICKNESS,
  SCHEDULE_TIMELINE_LOCKED_DAY,
} from "../style";
import { buildScheduleCellDropId } from "../dnd/scheduleDndIds";
import { toLocalDateKey } from "../../../../utils/datetime/core";

/**
 * One schedule row × calendar day cell (droppable target and optional context menu).
 *
 * @param {Object} props
 * @param {Object} props.row Flat schedule row (`id`, `type`, job/group metadata).
 * @param {Date} props.day Calendar day for this column.
 * @param {number} props.rowIdx Row index in the visible grid (striping).
 * @param {number} props.dayIdx Column index in the visible grid (striping).
 * @param {number} props.cellWidth Day column width in pixels.
 * @param {boolean} props.hasJob Row is tied to a job (enables droppable).
 * @param {boolean} props.inJobRange Day falls within the job’s active date range.
 * @param {boolean} props.isPlaceholder Spacer row (no interactions).
 * @param {string} props.bgTone Background utility class for striping.
 * @param {string} props.borderTone Border utility class for striping.
 * @param {boolean} [props.weekGapBorderAfter] Thick right border before a hidden weekend gap.
 * @param {(event: import('react').MouseEvent, payload: Object) => void} [props.onCellContextMenu] Empty-cell menu.
 * @param {boolean} [props.dayLocked] Dispatch locked day (muted, non-interactive).
 * @returns {JSX.Element}
 */
export default function TimelineGridCell({
  row,
  day,
  rowIdx,
  dayIdx,
  cellWidth,
  hasJob,
  inJobRange,
  isPlaceholder,
  bgTone,
  borderTone,
  weekGapBorderAfter = false,
  onCellContextMenu,
  dayLocked = false,
}) {
  const dayKey = toLocalDateKey(day);
  const { setNodeRef, isOver } = useDroppable({
    id: buildScheduleCellDropId(row.id, dayKey),
    data: { type: "schedule-cell", row, day, dayKey },
    disabled: isPlaceholder || !hasJob,
  });

  const fridayGapBorderColorClass = SCHEDULE_HIDDEN_WEEKEND_GAP_BORDER.replace(
    "border-",
    "border-r-"
  );
  const fridayGapRightRuleClass = weekGapBorderAfter
    ? `border-r-[${SCHEDULE_HIDDEN_WEEKEND_GAP_BORDER_THICKNESS}px] ${fridayGapBorderColorClass}`
    : "";
  const lockedTone = dayLocked
    ? ` ${SCHEDULE_TIMELINE_LOCKED_DAY.surfaceBgClass} pointer-events-none`
    : "";
  const cellClass = `h-full min-h-0 ${borderTone}${fridayGapRightRuleClass ? ` ${fridayGapRightRuleClass}` : ""} ${bgTone}${lockedTone}${
    isOver && hasJob && !isPlaceholder && !dayLocked ? " ring-2 ring-inset ring-primary/40" : ""
  }`;

  if (isPlaceholder || !onCellContextMenu) {
    return (
      <div
        ref={setNodeRef}
        className={cellClass}
        style={{ width: cellWidth, minWidth: cellWidth }}
      />
    );
  }

  if (!inJobRange) {
    return (
      <div
        ref={setNodeRef}
        className={`${cellClass} pointer-events-none`}
        style={{ width: cellWidth, minWidth: cellWidth }}
        aria-hidden
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`${cellClass} p-0`}
      style={{ width: cellWidth, minWidth: cellWidth }}
    >
      <div
        role="presentation"
        className="h-full w-full cursor-context-menu hover:bg-primary/5"
        onContextMenu={(e) =>
          onCellContextMenu(e, { row, rowIdx, day, dayIdx })
        }
      />
    </div>
  );
}
